import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../../../support/AxiosSupport';
import Spinner from '../../Spinner';
import {StepProps} from './types';

type ConsentKey = 'aiProfilingConsent' | 'recommendationsConsent' | 'photoProfilingConsent';

const CONSENT_ROWS: {key: ConsentKey; label: string; hint: string}[] = [
    {
        key: 'aiProfilingConsent',
        label: 'AI-анализ гардероба',
        hint: 'Система строит профили вещей и оценивает сочетаемость.',
    },
    {
        key: 'recommendationsConsent',
        label: 'Персональные рекомендации',
        hint: 'Образ дня, советы по покупкам и разбору гардероба.',
    },
    {
        key: 'photoProfilingConsent',
        label: 'Анализ фотографий',
        hint: 'Распознавание вещей по фото. Лица и биометрия не обрабатываются.',
    },
];

/**
 * Шаг «Согласия» (слот 9). Пишет в тот же контракт, что Центр приватности:
 * `GET /api/privacy/consent` и `PATCH /api/privacy/consent` по одному полю за раз — поэтому
 * переключатель сохраняется сразу, а не по кнопке «Далее» (иначе пропуск шага оставил бы
 * несохранённое согласие). В ответ шага кладём итоговый набор — для возобновления мастера.
 */
const StepConsents: React.FC<StepProps> = ({onChange}) => {
    const [consents, setConsents] = useState<Record<ConsentKey, boolean> | null>(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState<ConsentKey | null>(null);
    const [failed, setFailed] = useState(false);

    const publish = useCallback((next: Record<ConsentKey, boolean>) => {
        setConsents(next);
        onChange({...next});
    }, [onChange]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const api = await createAxiosInstance();
                const res = await api.get<Record<ConsentKey, boolean>>('/privacy/consent');
                if (!cancelled) {
                    publish({
                        aiProfilingConsent: res.data.aiProfilingConsent === true,
                        recommendationsConsent: res.data.recommendationsConsent === true,
                        photoProfilingConsent: res.data.photoProfilingConsent === true,
                    });
                }
            } catch {
                if (!cancelled) {
                    setFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // publish меняется вместе с onChange родителя; загрузка нужна однократно при монтировании
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggle = async (key: ConsentKey, value: boolean) => {
        if (!consents) {
            return;
        }
        setPending(key);
        setFailed(false);
        try {
            const api = await createAxiosInstance();
            await api.patch('/privacy/consent', {[key]: value});
            publish({...consents, [key]: value});
        } catch {
            setFailed(true);
        } finally {
            setPending(null);
        }
    };

    if (loading) {
        return <Spinner/>;
    }

    if (!consents) {
        return (
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="onboarding-consents-unavailable">
                Согласия сейчас недоступны — их можно выдать позже в настройках приватности.
            </p>
        );
    }

    return (
        <div className="space-y-4" data-testid="onboarding-step-consents">
            {CONSENT_ROWS.map(row => (
                <div key={row.key} className="flex items-start justify-between gap-4 min-h-[44px]">
                    <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{row.hint}</p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={consents[row.key]}
                        aria-label={row.label}
                        disabled={pending !== null}
                        onClick={() => toggle(row.key, !consents[row.key])}
                        data-testid={`onboarding-consent-${row.key}`}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 ${
                            consents[row.key] ? 'bg-brand-600' : 'bg-gray-200 dark:bg-ink-700'
                        }`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            consents[row.key] ? 'translate-x-6' : 'translate-x-1'
                        }`}/>
                    </button>
                </div>
            ))}
            {failed && (
                <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="onboarding-consent-error">
                    Не удалось сохранить согласие. Попробуйте ещё раз.
                </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Согласия можно изменить в любой момент в настройках приватности.
            </p>
        </div>
    );
};

export default StepConsents;
